/**
 * The dose section of the parent day view: time-slot groups with period
 * dividers, one bulk button per slot with two or more pending doses, and an
 * undo toast after every save.
 *
 * Saving goes through the server's bulk endpoint so a slot button is one
 * all-or-nothing request, and every save records the ids it created so the
 * toast's Annuler can soft-delete exactly those rows — including a bulk save.
 * On failure the toast turns into an error and the day refetches, so the
 * screen never claims a dose was recorded when it was not.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REASON_STATUS,
  deriveGivenStatus,
  groupDosesBySlot,
  type DoseReason,
} from '@toxofollow/shared';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { QueryState, clientId } from '../../components/form.js';
import { UndoToast, type ToastState } from '../../components/UndoToast.js';
import { DoseCard, type DoseItem } from './DoseCard.js';
import { useClock } from './useClock.js';

/**
 * The schedule endpoint names the slot's target time `time`; the card calls it
 * `scheduledTime` so it reads unambiguously next to `actualTime`. Map once
 * here rather than letting the API's field name leak through the UI.
 */
interface ApiSlot {
  orderId: string;
  concept: string;
  time: string;
  doseAmount: string | null;
  doseUnit: string | null;
  administration: { id: string; status: string; actualTime: string | null } | null;
}
interface ScheduleResp { date: string; slots: ApiSlot[] }
interface CreatedRow { id: string }
type Entry = Record<string, unknown>;

function toDoseItem(s: ApiSlot): DoseItem {
  return {
    orderId: s.orderId,
    concept: s.concept,
    scheduledTime: s.time,
    doseAmount: s.doseAmount,
    doseUnit: s.doseUnit,
    administration: s.administration,
  };
}

export function DoseList({ day }: { day: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const now = useClock();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const key = useMemo(() => ['parent-schedule', day], [day]);
  const q = useQuery({ queryKey: key, queryFn: () => api.get<ScheduleResp>(`/medications/schedule?date=${day}`) });

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: key });
  }, [qc, key]);

  /** Undo = soft-delete every row the action created. */
  const undo = useCallback(
    async (ids: string[]) => {
      setToast(null);
      try {
        await Promise.all(ids.map((id) => api.del(`/medications/administrations/${id}`)));
      } catch {
        setToast({ message: t('entry.saveFailed'), tone: 'error' });
      }
      refresh();
    },
    [refresh, t],
  );

  const save = useMutation({
    mutationFn: (v: { entries: Entry[]; message: string }) =>
      api.post<{ administrations: CreatedRow[] }>('/medications/administrations/bulk', { entries: v.entries }),
    onSuccess: (res, vars) => {
      const ids = res.administrations.map((r) => r.id);
      setToast({ message: vars.message, tone: 'ok', onUndo: () => void undo(ids) });
      refresh();
    },
    onError: () => {
      setToast({ message: t('entry.saveFailed'), tone: 'error' });
      refresh();
    },
  });

  const commit = (entries: Entry[], message: string) => {
    setEditing(null);
    save.mutate({ entries, message });
  };

  const entryFor = (item: DoseItem, status: string, actualTime: string | null): Entry => ({
    orderId: item.orderId,
    scheduledDate: day,
    scheduledTime: item.scheduledTime,
    status,
    actualDate: day,
    ...(actualTime ? { actualTime } : {}),
    ...(item.doseAmount ? { doseGiven: item.doseAmount, doseUnit: item.doseUnit ?? undefined } : {}),
    clientId: clientId('adm'),
  });

  const giveOne = (item: DoseItem, time: string) =>
    commit(
      [entryFor(item, deriveGivenStatus(item.scheduledTime, time), time)],
      t('entry.toastGiven', { name: t(`concept.${item.concept}`), time }),
    );

  const reasonOne = (item: DoseItem, reason: DoseReason) =>
    commit(
      [entryFor(item, REASON_STATUS[reason], null)],
      t('entry.toastReason', { name: t(`concept.${item.concept}`), reason: t(`reason.${reason}`) }),
    );

  const giveSlot = (items: DoseItem[], time: string) =>
    commit(
      items.map((i) => entryFor(i, deriveGivenStatus(i.scheduledTime, time), time)),
      t('entry.toastBulk', { n: items.length, time }),
    );

  /**
   * Modifier: the server refuses a second record for an already-logged slot, so
   * the old row is retracted first and the card reopens prefilled with its time.
   */
  const startEdit = async (item: DoseItem, cardKey: string) => {
    setEditing(cardKey);
    if (!item.administration) return;
    try {
      await api.del(`/medications/administrations/${item.administration.id}`);
    } catch {
      setToast({ message: t('entry.saveFailed'), tone: 'error' });
    }
    refresh();
  };

  const slots = useMemo(() => (q.data?.slots ?? []).map(toDoseItem), [q.data]);
  const groups = useMemo(() => groupDosesBySlot(slots, (s) => s.scheduledTime), [slots]);

  return (
    <>
      <QueryState
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={slots.length === 0}
        emptyMessage={t('day.noEntries')}
        errorFallback={t('common.error')}
      >
        <div className="flex flex-col gap-5">
          {groups.map((g) => {
            const pending = g.items.filter((i) => !i.administration);
            return (
              <section key={g.time} className="flex flex-col gap-2.5">
                {g.showDivider && (
                  <div
                    role="separator"
                    aria-label={t(`daypart.${g.part}`)}
                    className="mb-1.5 mt-[18px] flex items-center gap-3.5"
                  >
                    <span aria-hidden="true" className="h-0.5 flex-1 rounded-sm bg-[#C9D1D9]" />
                    <span className="text-[13px] font-bold uppercase tracking-[0.12em] text-[#3A4652]">
                      {t(`daypart.${g.part}`)}
                    </span>
                    <span aria-hidden="true" className="h-0.5 flex-1 rounded-sm bg-[#C9D1D9]" />
                  </div>
                )}

                <div className="flex min-h-[44px] flex-wrap items-center justify-between gap-3 px-0.5">
                  <h2 className="m-0 text-sm font-[650] uppercase tracking-[0.06em] text-[#56616D]">
                    {g.time} · {t(`daypart.${g.part}`)}
                  </h2>
                  {pending.length >= 2 && (
                    <button
                      type="button"
                      disabled={save.isPending}
                      onClick={() => giveSlot(pending, g.time)}
                      className="min-h-[44px] rounded-[10px] border-[1.5px] border-brand bg-white px-4 text-[15px] font-semibold text-brand disabled:opacity-50"
                    >
                      {t('entry.bulkGive', { n: pending.length, time: g.time })}
                    </button>
                  )}
                </div>

                {g.items.map((item) => {
                  const cardKey = `${item.orderId}-${item.scheduledTime}`;
                  return (
                    <DoseCard
                      key={cardKey}
                      item={item}
                      now={now}
                      busy={save.isPending}
                      editing={editing === cardKey}
                      onGive={(time) => giveOne(item, time)}
                      onReason={(reason) => reasonOne(item, reason)}
                      onEdit={() => void startEdit(item, cardKey)}
                    />
                  );
                })}
              </section>
            );
          })}
        </div>
      </QueryState>

      <UndoToast toast={toast} onDismiss={() => setToast(null)} />
    </>
  );
}
